import { useLayoutEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Card } from '@components/card';
import { useWindowSize } from '@components/component-helpers';
import { EmptyState } from '@components/empty-state';
import { IconAppSmall } from '@components/icons/app-small';
import { IconSearch } from '@components/icons/search';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useIsMobile } from '@hooks/use-is-mobile';
import { theme } from '@styles/theme';
import { throttle } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { Box } from 'components/Box';
import { JobOptions } from 'components/JobOptions';
import { Loading } from 'components/Loading';
import { PageContent } from 'components/Page';
import { ActionsContainer } from 'components/styles';
import { Tab, TabList, TabPanel, TabPanelList, useTabState } from 'components/Tabs';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

export const Jobs = observer(() => {
  const { uiSettings, jobStore, t } = useStore();
  const { chosenTab, changeTab, tabText } = uiSettings.jobs;
  const tabState = useTabState({ selectedId: chosenTab });

  useLayoutEffect(() => {
    jobStore.load(tabText);
  }, [tabText, jobStore]);

  const onScroll = useMemo(
    () =>
      throttle((e) => {
        if (!uiSettings.jobs.nextUrls[tabText]) return;
        const el = e.target;
        const maxHeight = el.scrollHeight;
        const heightWithScroll = el.clientHeight + el.scrollTop;
        if (maxHeight - heightWithScroll < 80) {
          jobStore.loadNext(tabText);
        }
      }, 200),
    [jobStore, tabText, uiSettings.jobs.nextUrls]
  );

  return (
    <>
      <TabContainer>
        <TabList
          variant="pill"
          state={tabState}
          label="Job state selection"
          onSelect={changeTab}
          collapseOnMobile
        >
          <Tab>{t('jobs.tabs.published')}</Tab>
          <Tab>{t('jobs.tabs.drafts')}</Tab>
          <Tab>{t('jobs.tabs.expired')}</Tab>
          <Tab>{t('jobs.tabs.archived')}</Tab>
        </TabList>
      </TabContainer>
      <PageContent
        scroll
        onScroll={(e) => {
          e.persist();
          onScroll(e);
        }}
      >
        {jobStore.loading && <Loading delay={1000} />}
        <TabPanelList state={tabState}>
          <TabPanel>
            <JobList jobs={jobStore.published} type="published" />
          </TabPanel>
          <TabPanel>
            <JobList jobs={jobStore.drafts} type="drafts" />
          </TabPanel>
          <TabPanel>
            <JobList jobs={jobStore.expired} type="expired" />
          </TabPanel>
          <TabPanel>
            <JobList jobs={jobStore.archived} type="archived" />
          </TabPanel>
        </TabPanelList>
      </PageContent>
    </>
  );
});

const JobList = observer(({ jobs, type }) => {
  const { uiSettings, jobStore, t } = useStore();
  const isMobile = useIsMobile();
  const { searchStr } = uiSettings.jobs;
  if (jobs.length === 0 && jobStore.loaded && !jobStore.loading) {
    if (searchStr) {
      return (
        <EmptyState
          skipMargin
          header={t('jobs.nosearchjobs')}
          content={t('jobs.nosearchjobs.subtitle')}
          icon={<IconSearch size={80} />}
        />
      );
    } else {
      return (
        <EmptyState
          skipMargin
          header={t(`jobs.nojobs.${type}`)}
          content={t(`jobs.alljobs.${type}`)}
          icon={<IconSearch size={80} />}
        />
      );
    }
  }

  const EntryComponent = isMobile ? MobileJobListEntry : JobListEntry;

  return (
    <JobListContainer>
      {jobs.map((job) => (
        <EntryComponent key={job.id} job={job} />
      ))}
    </JobListContainer>
  );
});

const MobileJobListEntry = observer(({ job }) => {
  const { lang, t } = useStore();
  return (
    <Link to={job.detailUrl}>
      <MobileInfoCard>
        <Card spaceX={6} spaceY={4}>
          <Stack space={4}>
            <Text font="bold18" text={job.title} />
            <Stack space={0}>
              <Stack space={0} orientation="horizontal">
                <Text
                  font="regular14"
                  color="purple80"
                  text={t('jobs.list.location')}
                  style={{ width: '5.5rem' }}
                />
                <Text font="regular14" color="purple80" text={job.locationDisplay(lang)} />
              </Stack>
              <Stack space={0} orientation="horizontal">
                <Text
                  font="regular14"
                  color="purple80"
                  text={t('jobs.list.deadline')}
                  style={{ width: '5.5rem' }}
                />
                <Text font="regular14" color="purple80" children={job.deadlineDisplay} />
              </Stack>
              <Text font="regular14" color="purple80" text={job.dateScheduledPublishDisplay} />
            </Stack>
          </Stack>
        </Card>
        <Stack space={0} orientation="horizontal">
          <MobileApplicantsCountBox w="50%" borderRight>
            <Stack space={4} orientation="horizontal" align="center">
              <Text font="heading2" text={job.numApplications} />
              <Text font="regular14" color="purple80" text={t('jobs.list.applicants')} />
            </Stack>
          </MobileApplicantsCountBox>
          <MobileApplicantsCountBox
            w="50%"
            bg={job.numNewApplications > 0 ? 'green100' : 'purple100'}
          >
            <Stack space={4} orientation="horizontal" align="center">
              <Text font="heading2" color="white100" text={job.numNewApplications} />
              <Text font="regular14" color="white100" text={t('jobs.list.applicantsnew')} />
            </Stack>
          </MobileApplicantsCountBox>
        </Stack>
      </MobileInfoCard>
    </Link>
  );
});

const MobileApplicantsCountBox = styled(Box)((p) => ({
  borderTop: `0.0625rem solid ${p.theme.colors.purpleTrans5}`,
  backgroundColor: p.bg ? p.theme.colors[p.bg] : 'transparent',
  padding: '0 1.5rem',
  height: '2.5rem',
  display: 'flex',
  alignItems: 'center',
  borderRadius: '0 0 0.25rem 0',
  ...(p.borderRight && {
    borderRight: `0.0625rem solid ${p.theme.colors.purpleTrans5}`,
  }),
}));

const JobListEntry = observer(({ job }) => {
  function jobDateText(job) {
    if (job.isPublished && !job.isArchived && !job.isExpired) {
      return job.datePublishedDisplay;
    } else if (!job.isPublished && !job.isArchived) {
      return job.dateScheduledPublishDisplay
        ? job.dateScheduledPublishDisplay
        : job.dateCreatedDisplay;
    } else if (job.isPublished && !job.isArchived && job.isExpired) {
      return job.dateExpiredDisplay;
    } else {
      return job.dateArchivedDisplay;
    }
  }
  const { lang, t } = useStore();
  const { width } = useWindowSize();
  const { hasRolePermission } = usePermissions();

  const numLabels = job.labelsDisplay.length;

  const maxLabelsOnList = 5;

  const restOfLabels = job.labelsDisplay
    .slice(maxLabelsOnList)
    .map((label) => label.title)
    .join(', ');

  return (
    <JobCard to={job.detailUrl} scheduled={job.dateScheduledPublish ? true : undefined}>
      <Stack space={2}>
        <Stack orientation="horizontal" justify="space-between" align="center">
          <Stack>
            <JobTitle>
              <Stack>
                <Text font="bold16" className="job-title" data-cy={job.title} text={job.title} />
                <Stack space={2}>
                  <Stack>
                    <Text
                      font="regular14"
                      color="textSecondary"
                      text={
                        <>
                          {job.workTypeDisplay(lang) && (
                            <>
                              <span>{job.workTypeDisplay(lang)}</span>
                              <span style={{ margin: '0 4px' }}>&bull;</span>
                            </>
                          )}
                          <span>{job.locationDisplay(lang)}</span>
                          {!job.isExpired && !job.isArchived && (
                            <>
                              <span style={{ margin: '0 4px' }}>&bull;</span>
                              <span>{job.deadlineDisplay}</span>
                            </>
                          )}
                        </>
                      }
                    />
                    <Text font="regular14" color="textSecondary" text={jobDateText(job)} />
                  </Stack>
                </Stack>
              </Stack>
            </JobTitle>
          </Stack>
          <Stack orientation="horizontal" justify="flex-end" align="center">
            {width > 1100 && job.visibility === 'private' && (
              <Tooltip content={t('job.tips.notvisible')}>
                <Tag size="small" variant="purple" text={t('jobs.list.private')} />
              </Tooltip>
            )}
            {job.state === 'published' && (
              <EntryBox
                number={job.numNewApplications}
                color={job.numNewApplications > 0 ? 'elementGreen100' : 'textPrimary'}
                text={t('jobs.list.applicantsnew')}
              />
            )}
            <EntryBox number={job.numApplications} text={t('jobs.list.applicants')} />
            {job.state !== 'published' && (
              <>
                <EntryBox
                  number={job.numActiveApplications}
                  text={t('jobs.list.activeapplicants')}
                />
                {width > 850 && (
                  <EntryBox
                    number={job.numRejectedApplications}
                    text={t('jobs.list.rejectedapplicants')}
                  />
                )}
                {width > 900 && (
                  <EntryBox
                    number={job.numHiredApplications}
                    text={t('jobs.list.hiredapplicants')}
                  />
                )}
              </>
            )}
            {job.state === 'published' && (
              <>
                {width > 800 && <EntryBox number={job.daysOpen} text={t('jobs.list.daysopen')} />}
                {width > 900 && (
                  <EntryBox
                    number={job.deadline ? job.daysUntilDeadline : '&bull;'}
                    color={
                      job.deadline && job.daysUntilDeadline < 8 ? 'elementOrange100' : 'textPrimary'
                    }
                    text={t('jobs.list.daysleft')}
                  />
                )}
              </>
            )}
            {width > 1050 && (
              <EntryBox
                border={false}
                number={job.activeFollowers.length}
                text={t('jobs.list.access')}
              />
            )}
            {hasRolePermission('job:options') && (
              <Stack
                space={0}
                orientation="horizontal"
                justify="center"
                style={{ width: '2.5rem' }}
              >
                <ActionsContainer>
                  <JobOptions hideAddStage job={job} />
                </ActionsContainer>
              </Stack>
            )}
          </Stack>
        </Stack>
        {numLabels > 0 && (
          <Stack space={1} justify="flex-start" orientation="horizontal">
            {job.labelsDisplay.slice(0, 5).map((label) => (
              <Tag
                key={label.id}
                size="small"
                variant="customColor"
                color={label.color}
                text={label.title}
                icon={<IconAppSmall size={12} />}
              />
            ))}
            {numLabels > maxLabelsOnList && (
              <Text
                font="regular12"
                color="textHelper"
                text={t('jobs.list.more', numLabels - maxLabelsOnList)}
                tooltip={restOfLabels}
              />
            )}
          </Stack>
        )}
      </Stack>
    </JobCard>
  );
});

const EntryBox = observer(({ number, text, border = true, color = 'textPrimary' }) => {
  return (
    <EntryContainer border={border}>
      <Stack space={0} align="center">
        <Text
          color={color}
          font="heading2"
          text={
            typeof number === 'string' ? (
              <span dangerouslySetInnerHTML={{ __html: number }} />
            ) : (
              <span>{number > 0 ? number : 0}</span>
            )
          }
        />
        <Text color="textSecondary" font="regular12" text={text} />
      </Stack>
    </EntryContainer>
  );
});

const JobListContainer = styled.div((p) => ({
  [p.theme.mobileQuery]: {
    a: {
      display: 'block',
    },
  },
  'a:not(:first-child)': {
    marginTop: pxToRem(8),
    [p.theme.mobileQuery]: {
      marginTop: pxToRem(16),
    },
  },
}));

const EntryContainer = styled.div((p) => ({
  padding: `${pxToRem(12)} ${pxToRem(24)}`,
  ...(p.border && {
    borderRight: `${pxToRem(1)} solid ${theme.color.border10}`,
  }),
}));

const JobCard = styled(Link)((p) => ({
  backgroundColor: p.scheduled ? theme.color.orangeTrans10 : theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  display: 'block',
  alignItems: 'center',
  padding: `${theme.space[4]} ${theme.space[6]}`,
  border: `${pxToRem(1)} solid ${theme.color.border10}`,
  boxSizing: 'border-box',
  transition: 'all 150ms ease-in-out',
  ':hover, :focus': {
    border: `${pxToRem(1)} solid ${theme.color.border20}`,
    boxShadow: theme.shadow.low,
    '.job-title': {
      transition: '150ms ease',
      color: p.theme.colors.blue100,
    },
  },
}));

const JobTitle = styled.div({
  flexGrow: 1,
  margin: 'auto 0',
});

const MobileInfoCard = styled.div((p) => ({
  backgroundColor: p.theme.colors.backgroundLightDark,
  borderRadius: pxToRem(8),
}));

const TabContainer = styled.div({
  backgroundColor: theme.color.backgroundLightDark,
  padding: `${pxToRem(16)} ${pxToRem(24)}`,
  borderBottom: `${pxToRem(1)} solid ${theme.color.border10}`,
  '@media(max-width: 768px)': {
    padding: `${pxToRem(0)} ${pxToRem(24)} ${pxToRem(16)} ${pxToRem(24)}`,
  },
});
